import { AlertCircle, Layers } from 'lucide-react'
import type { CSSProperties, KeyboardEvent, Ref } from 'react'

import type { PoolCardModel } from '@/features/scheduler/poolCards'
import { cn } from '@/lib/cn'
import styles from './PoolCard.module.scss'

const STOCK_LABEL: Record<PoolCardModel['stock'], string> = {
  ok: 'Estoque em dia',
  low: 'Estoque baixo',
  empty: 'Sem estoque',
  idle: 'Sem canais ligados',
}

interface PoolCardProps {
  card: PoolCardModel
  /** O pool aberto embaixo do carrossel. */
  selected: boolean
  onSelect: () => void
  onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void
  ref?: Ref<HTMLButtonElement>
}

/**
 * Um pool no carrossel da tela de Pools: uma aba compacta (220×84) com nome,
 * factory, idioma e estoque. Horário, canais, ações e o aviso inteiro ficam
 * no detalhe, que é o painel desta aba.
 */
export function PoolCard({ card, selected, onSelect, onKeyDown, ref }: PoolCardProps) {
  const quiet = !card.enabled || card.stock === 'idle'
  const status = !card.enabled ? 'Pausado' : card.stock === 'idle' ? 'Sem canais' : null

  return (
    <button
      ref={ref}
      type="button"
      role="tab"
      id={`pool-tab-${card.slug}`}
      aria-selected={selected}
      aria-controls="pool-panel"
      aria-label={`${card.name}, ${status ?? STOCK_LABEL[card.stock]}`}
      tabIndex={selected ? 0 : -1}
      className={cn(styles.card, selected && styles.selected, quiet && styles.quiet)}
      style={{ '--accent': card.accent } as CSSProperties}
      onClick={onSelect}
      onKeyDown={onKeyDown}
    >
      <span className={styles.head}>
        <span className={styles.tile} aria-hidden>
          <Layers />
        </span>
        <span className={styles.name}>{card.name}</span>
        <span
          className={cn(styles.dot, styles[`dot-${card.stock}`])}
          aria-hidden
          title={STOCK_LABEL[card.stock]}
        />
      </span>
      <span className={styles.sub}>
        {[card.factoryLabel, card.language].filter(Boolean).join(' · ')}
      </span>
      <span className={styles.line}>
        {status ? (
          <span className={styles.tag}>{status}</span>
        ) : (
          <>
            {card.alert ? <AlertCircle className={styles.alert} aria-hidden /> : null}
            <span className={styles.lineText} title={card.alert ?? card.stockText}>
              <strong>{card.available}</strong> disp ·{' '}
              <span className={styles[`level-${card.stock}`]}>{card.stockText}</span>
            </span>
          </>
        )}
      </span>
      <span className={styles.bar} aria-hidden>
        <i
          className={styles[`fill-${card.stock}`]}
          style={{ width: `${String(card.stockPercent)}%` }}
        />
      </span>
    </button>
  )
}

/** O último item do carrossel: abre a criação de pool. */
export function CreatePoolCard({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className={cn(styles.card, styles.create)} onClick={onClick}>
      <span className={styles.createLabel}>+ Novo pool</span>
    </button>
  )
}
